import { ChildrenOutletContexts, RouterOutlet } from "@angular/router";
import { Component, inject } from "@angular/core";
import { CstLanguageService } from "./services/cst-language.service";
import {
  SaveChangesOnDeactivate,
  shouldWarnBeforeUnload,
} from "./guards/unsaved-changes.guard";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false,
  host: {
    "(window:beforeunload)": "onBeforeUnload($event)",
  },
})
export class AppComponent {
  private _outletContexts = inject(ChildrenOutletContexts);

  constructor() {
    const langService = inject(CstLanguageService);
    langService.switchAppLanguage(langService.currentSelectedLanguage || langService.browserLanguage);
  }

  public onBeforeUnload(event: BeforeUnloadEvent): void {
    const guardedComponents = this._findSaveChangesComponents(this._outletContexts);
    const hasUnsavedChanges = guardedComponents.some((component) =>
      shouldWarnBeforeUnload(component),
    );

    if (!hasUnsavedChanges) {
      return;
    }

    event.preventDefault();
    Reflect.set(event, "returnValue", "");
  }

  private _findSaveChangesComponents(
    contexts: ChildrenOutletContexts,
  ): SaveChangesOnDeactivate[] {
    const found: SaveChangesOnDeactivate[] = [];
    const contextMap = (contexts as any).contexts as
      | Map<string, { outlet?: RouterOutlet; children?: ChildrenOutletContexts }>
      | undefined;

    if (!contextMap) {
      return found;
    }

    for (const [, context] of contextMap.entries()) {
      const component = context?.outlet?.component;
      if (this._isSaveChangesOnDeactivate(component)) {
        found.push(component);
      }

      if (context?.children) {
        found.push(...this._findSaveChangesComponents(context.children));
      }
    }

    return found;
  }

  private _isSaveChangesOnDeactivate(
    component: unknown,
  ): component is SaveChangesOnDeactivate {
    return (
      !!component &&
      typeof (component as SaveChangesOnDeactivate).hasUnsavedChanges === "function" &&
      typeof (component as SaveChangesOnDeactivate).canSaveChanges === "function" &&
      typeof (component as SaveChangesOnDeactivate).saveChanges === "function"
    );
  }

}
